/** Reusable console widgets: 7-segment LED, rolling odometer, sparkline, rating gauge, bars. */
import { h, svg, setText } from './dom';

// ---------------------------------------------------------------------------
// 7-segment LED display
// ---------------------------------------------------------------------------

const DIGIT_W = 12, DIGIT_H = 22, DIGIT_GAP = 3, COLON_W = 5;
const hseg = (cy: number) => `2.6,${cy} 3.6,${cy - 1} 8.4,${cy - 1} 9.4,${cy} 8.4,${cy + 1} 3.6,${cy + 1}`;
const vseg = (cx: number, y1: number, y2: number) => `${cx},${y1} ${cx + 1},${y1 + 1} ${cx + 1},${y2 - 1} ${cx},${y2} ${cx - 1},${y2 - 1} ${cx - 1},${y1 + 1}`;
const SEGS: Record<string, string> = {
  a: hseg(1.6), g: hseg(11), d: hseg(20.4),
  f: vseg(1.6, 2.4, 10.4), b: vseg(10.4, 2.4, 10.4), e: vseg(1.6, 11.6, 19.6), c: vseg(10.4, 11.6, 19.6),
};
const GLYPHS: Record<string, string> = {
  '0': 'abcdef', '1': 'bc', '2': 'abged', '3': 'abgcd', '4': 'fgbc', '5': 'afgcd', '6': 'afgedc',
  '7': 'abc', '8': 'abcdefg', '9': 'abcdfg', '-': 'g', ' ': '', 'H': 'bcefg', 'F': 'aefg', 'E': 'adefg', 'P': 'abefg',
};

export interface SegmentDisplay { el: SVGSVGElement; set(text: string): void }

/** `pattern` uses '8' for a digit cell and ':' for a colon, e.g. "88:88". */
export function createSegmentDisplay(pattern: string, cls = ''): SegmentDisplay {
  let x = 0;
  const cells: { segs: Record<string, SVGPolygonElement> }[] = [];
  const colons: SVGGElement[] = [];
  const root = svg('svg', { class: `sd-seg ${cls}`, 'aria-hidden': 'true' });
  for (const ch of pattern) {
    if (ch === ':') {
      const g = svg('g', { class: 'sd-seg__colon on', transform: `translate(${x},0)` }, [
        svg('circle', { cx: COLON_W / 2 - 0.5, cy: 7, r: 1.25 }), svg('circle', { cx: COLON_W / 2 - 0.5, cy: 15, r: 1.25 }),
      ]);
      root.append(g); colons.push(g); x += COLON_W;
    } else {
      const g = svg('g', { transform: `translate(${x},0) skewX(-6)` });
      const segs: Record<string, SVGPolygonElement> = {};
      for (const [k, pts] of Object.entries(SEGS)) {
        const p = svg('polygon', { points: pts, class: 'sd-seg__s' });
        g.append(p); segs[k] = p;
      }
      root.append(g); cells.push({ segs }); x += DIGIT_W + DIGIT_GAP;
    }
  }
  root.setAttribute('viewBox', `-1 0 ${x - DIGIT_GAP + 2} ${DIGIT_H}`);
  let last = '';
  return {
    el: root,
    set(text) {
      const digits = text.replace(/:/g, '').padStart(cells.length, ' ').slice(-cells.length);
      if (digits === last) return;
      last = digits;
      cells.forEach((c, i) => {
        const on = GLYPHS[digits[i]!.toUpperCase()] ?? '';
        for (const [k, el] of Object.entries(c.segs)) {
          const want = on.includes(k);
          if (el.classList.contains('on') !== want) el.classList.toggle('on', want);
        }
      });
    },
  };
}

// ---------------------------------------------------------------------------
// Rolling odometer
// ---------------------------------------------------------------------------

export interface Odometer { el: HTMLElement; set(n: number): void }

export function createOdometer(cls = ''): Odometer {
  const el = h('span', { class: `sd-odo ${cls}` });
  const cells: { el: HTMLElement; strip?: HTMLElement; ch: string }[] = [];
  let last = '';
  const build = (s: string) => {
    el.textContent = '';
    cells.length = 0;
    for (const ch of s) {
      if (/\d/.test(ch)) {
        const strip = h('span', { class: 'sd-odo__strip' }, '0123456789'.split('').map((d) => h('span', { text: d })));
        const cell = h('span', { class: 'sd-odo__digit' }, [strip]);
        strip.style.transform = `translateY(${-Number(ch) * 10}%)`;
        el.append(cell); cells.push({ el: cell, strip, ch });
      } else {
        const sep = h('span', { class: 'sd-odo__sep', text: ch });
        el.append(sep); cells.push({ el: sep, ch });
      }
    }
  };
  return {
    el,
    set(n) {
      const s = Math.max(0, Math.round(n)).toLocaleString('en-GB');
      if (s === last) return;
      if (s.length !== last.length) { build(s); last = s; return; }
      for (let i = 0; i < s.length; i++) {
        const c = cells[i]!;
        if (c.ch === s[i]) continue;
        c.ch = s[i]!;
        if (c.strip) c.strip.style.transform = `translateY(${-Number(c.ch) * 10}%)`;
        else c.el.textContent = c.ch;
      }
      last = s;
    },
  };
}

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------

export interface Sparkline { el: SVGSVGElement; set(values: readonly number[]): void }

export function createSparkline(w = 120, h = 28, cls = ''): Sparkline {
  const area = svg('path', { class: 'sd-spark__area' });
  const line = svg('polyline', { class: 'sd-spark__line' });
  const dot = svg('circle', { class: 'sd-spark__dot', r: 2 });
  const el = svg('svg', { class: `sd-spark ${cls}`, viewBox: `0 0 ${w} ${h}`, preserveAspectRatio: 'none' }, [area, line, dot]);
  return {
    el,
    set(values) {
      if (values.length < 2) return;
      let lo = Infinity, hi = -Infinity;
      for (const v of values) { lo = Math.min(lo, v); hi = Math.max(hi, v); }
      const span = hi - lo || 1;
      const pts = values.map((v, i) => [(i / (values.length - 1)) * w, h - 3 - ((v - lo) / span) * (h - 6)] as const);
      const str = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
      line.setAttribute('points', str);
      area.setAttribute('d', `M0,${h} L${str.replace(/ /g, ' L')} L${w},${h} Z`);
      const [lx, ly] = pts[pts.length - 1]!;
      dot.setAttribute('cx', lx.toFixed(1)); dot.setAttribute('cy', ly.toFixed(1));
    },
  };
}

// ---------------------------------------------------------------------------
// Rating gauge (270° arc)
// ---------------------------------------------------------------------------

export interface Gauge { el: HTMLElement; set(rating: number, letter: string, color: string): void }

export function createGauge(): Gauge {
  const R = 42, C = 2 * Math.PI * R, ARC = C * 0.75;
  const ticks = svg('g', { class: 'sd-gauge__ticks' });
  for (let i = 0; i <= 20; i++) {
    const a = (135 + (270 * i) / 20) * (Math.PI / 180);
    const r1 = i % 5 === 0 ? 47 : 48.5, r2 = 51;
    ticks.append(svg('line', { x1: 60 + Math.cos(a) * r1, y1: 60 + Math.sin(a) * r1, x2: 60 + Math.cos(a) * r2, y2: 60 + Math.sin(a) * r2, class: i % 5 === 0 ? 'major' : '' }));
  }
  const track = svg('circle', { cx: 60, cy: 60, r: R, class: 'sd-gauge__track', 'stroke-dasharray': `${ARC} ${C}`, transform: 'rotate(135 60 60)' });
  const fill = svg('circle', { cx: 60, cy: 60, r: R, class: 'sd-gauge__fill', 'stroke-dasharray': `0 ${C}`, transform: 'rotate(135 60 60)' });
  const letter = h('div', { class: 'sd-gauge__letter', text: '–' });
  const num = h('div', { class: 'sd-gauge__num', text: '0' });
  const el = h('div', { class: 'sd-gauge' }, [
    svg('svg', { viewBox: '0 0 120 120' }, [ticks, track, fill]),
    h('div', { class: 'sd-gauge__center' }, [letter, num]),
  ]);
  let lastLetter = '';
  return {
    el,
    set(rating, l, color) {
      const v = Math.max(0, Math.min(100, rating));
      fill.setAttribute('stroke-dasharray', `${((ARC * v) / 100).toFixed(2)} ${C}`);
      el.style.setProperty('--g-color', color);
      setText(num, String(Math.round(v)));
      if (l !== lastLetter) {
        setText(letter, l);
        if (lastLetter) { letter.classList.remove('bump'); void letter.offsetWidth; letter.classList.add('bump'); }
        lastLetter = l;
      }
    },
  };
}
